import { createContext, useContext, useId, useState, type ReactNode } from "react";

/**
 * Component-level guard for the article TL;DR card.
 *
 * `BlogTldrBoundary` wraps the article subtree and provides a per-render
 * "claim slot" that the first `BlogTldr` inside the subtree takes. Any
 * further `BlogTldr` instances rendered under the same boundary become
 * no-ops, so duplicate TL;DR data (e.g. an authored `## TL;DR` plus a
 * synthesised one, or accidental double-inclusion) can never render twice
 * on the same page.
 *
 * The slot is reset at the top of every boundary render, so the guard
 * survives re-renders, Strict Mode double-invocation, and route changes.
 */

type TldrSlot = { claimedId: string | null };

const TldrSlotContext = createContext<TldrSlot | null>(null);

export function BlogTldrBoundary({ children }: { children: ReactNode }) {
  // A single per-mount slot, seeded once with useState so React never
  // discards it between Strict Mode double-invokes. The slot stores the
  // useId() of the first BlogTldr that claims it, so subsequent renders
  // (including Strict Mode's second render pass and client hydration) are
  // idempotent: the same BlogTldr keeps rendering, siblings keep returning
  // null. Do NOT reset this on every render — mutating during render is
  // what caused earlier renders to lose the card after hydration.
  const [slot] = useState<TldrSlot>(() => ({ claimedId: null }));
  return <TldrSlotContext.Provider value={slot}>{children}</TldrSlotContext.Provider>;
}

export function BlogTldr({ tldr }: { tldr: string | null | undefined }) {
  const slot = useContext(TldrSlotContext);
  const id = useId();
  const trimmed = typeof tldr === "string" ? tldr.trim() : "";
  if (!trimmed) return null;

  if (slot) {
    // First BlogTldr to reach here claims the slot; further siblings
    // (different useId) become no-ops. Same instance re-rendering
    // (Strict Mode, hydration, parent re-render) keeps rendering.
    if (slot.claimedId === null) slot.claimedId = id;
    if (slot.claimedId !== id) {
      if (
        typeof window !== "undefined" &&
        process.env.NODE_ENV !== "production"
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          "[BlogTldr] Duplicate TL;DR suppressed — only the first instance renders per page.",
        );
      }
      return null;
    }
  }

  return (
    <header className="demo-card demo-card-accent demo-meta-card">
      <div className="blog-tldr-summary">
        <p className="blog-tldr-heading">TL;DR</p>
        <p className="demo-meta-card-description">{trimmed}</p>
      </div>
    </header>
  );
}